import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

interface CreateReportListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isPending?: boolean
  onConfirm: (name: string) => void
}

export function CreateReportListDialog({
  open,
  onOpenChange,
  isPending,
  onConfirm,
}: Readonly<CreateReportListDialogProps>) {
  const { t } = useTranslation()
  const [name, setName] = useState("")

  const handleConfirm = () => {
    if (name.trim() && !isPending) {
      onConfirm(name.trim())
    }
  }

  const handleCancel = () => {
    if (isPending) return
    setName("")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onInteractOutside={isPending ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>{t("dialogs.createList.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="report-list-name">{t("dialogs.createList.nameLabel")}</Label>
            <Input
              id="report-list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConfirm()
                if (e.key === "Escape") handleCancel()
              }}
              placeholder={t("dialogs.createList.namePlaceholder")}
              disabled={isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t("actions.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!name.trim() || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.loading")}
              </>
            ) : (
              t("reportList.create")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
